import { useContext, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";
import {
  CheckIcon,
  CircleAlertIcon,
  CircleCheckIcon,
  EllipsisIcon,
  InfoIcon,
  LightbulbIcon,
  LockIcon,
  PercentIcon,
} from "lucide-react";

import { initUseQuery } from "simplydo/core/query";
import type { OpenAPI } from "simplydo/interfaces";
import { useThrottle } from "simplydo/react/hooks";
import { css, cx, styled } from "simplydo/react/styled";
import { Accordion } from "simplydo/react/ui/accordion";
import { Button } from "simplydo/react/ui/button";
import { Icon, IconData } from "simplydo/react/ui/icon";
import { Input } from "simplydo/react/ui/input";
import { Label } from "simplydo/react/ui/label";
import { List, ListItem } from "simplydo/react/ui/list";
import { Popup } from "simplydo/react/ui/popup";
import { TextArea } from "simplydo/react/ui/textarea";

import api from "api";
import { useCurrentUser } from "utils/currentUser";

import { useIdeaQueryParams } from "components/challenges/Challenge/Ideas/useIdeaQueryParams";
import { Checkbox } from "components/lib/UI";

import { getImpactTypeIcon } from "../Ideas/ImpactStatements/impactIcons";
import { formatImpact } from "../Ideas/ImpactStatements/statementUtils";
import type { OrganisationImpact } from "../Ideas/types";
import type { ILaneData } from "./Board";
import { DropdownLaneSettings } from "./LaneSettings";
import { MetaInfo } from "./MetaContainer";
import { ProjectBoardContext } from "./ProjectBoardContext";
import type { LaneImpactInput, ProjectBoardLaneUpdateBody } from "./types";

const StyledLaneHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  position: relative;

  &.is-selecting > *:not(.selection-box) {
    opacity: 0.6;
    cursor: pointer;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  margin-right: 5px;
  h3 {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    -moz-line-clamp: 2;

    font-size: 1.2em;
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }
  span {
    font-weight: 375;
  }
`;

const LaneDescription = styled.p`
  color: gray;
  width: 100%;
  word-wrap: break-word;
  word-break: normal;
  white-space: normal;
  margin: 2.5px 0;
  text-align: left;
`;

const invisibleButtonClass = css`
  &&& {
    background: #f1f2f4;
    &:hover {
      background: #e0e1e2;
    }
  }
`;

const Header = styled.div`
  display: flex !important;
  flex-direction: column !important;
  gap: 8px;
  background: rgb(241, 242, 244);
  padding: 10px 8px;
  .selection-box {
    opacity: 1;
    top: 0px;
    right: 0px;
    z-index: 10;
    display: flex;
    justify-content: space-between;
    background: #fff;
    border-radius: 5px;
    box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.2);
    padding: 5px;
    align-items: center;
    p {
      display: block;
      margin: 0 5px 0 5px;
    }
  }
`;

const StyledTitle = styled.h3`
  &.is-editable {
    cursor: pointer;
  }
`;

const circleIconLabelClass = css`
  i {
    margin: 0 !important;
  }
`;

const impactAccordionClass = css`
  &&& {
    background: #f1f2f4;

    .title {
      user-select: none;
    }
  }
`;

const HeaderImpactMeasure = ({
  icon,
  value,
  impactType,
  impactName,
  impactEffect,
}: {
  icon?: IconData;
  value: string | number | null;
  impactType?: OpenAPI.Schemas["OrganisationImpact"]["type"];
  impactName: string;
  impactEffect?: OpenAPI.Schemas["OrganisationImpact"]["effect"];
}) => {
  const { t } = useTranslation();

  const orgImpact = {
    type: impactType,
    effect: impactEffect,
  } as OrganisationImpact;
  const usingValue = formatImpact(orgImpact, {
    value: value ?? "",
  } as LaneImpactInput);

  const annualImpactValue = (impact: LaneImpactInput) => {
    return formatImpact(impact.ownerImpact, impact, { annually: true });
  };

  const generateImpactText = (impact: LaneImpactInput) => {
    const effect = impact?.ownerImpact?.effect;
    const effectText =
      effect === "cost" ? "cost" : effect === "saving" ? "saving" : "impact";
    let text = `The ${t("generic.ideas")} in this lane will have a total ${effectText} of up to `;
    text += formatImpact(impact.ownerImpact, impact);

    if (!impact?.frequency || impact?.frequency === "one-off") {
      text += " once";
    } else {
      text += ` ${impact?.frequency}`;
      if (impact?.frequency !== "annually") {
        text += ` (${annualImpactValue(impact)} annually)`;
      }
    }
    text += ".";

    return (
      <>
        <p>
          <strong>{impact?.ownerImpact?.name}</strong>
        </p>
        <p>{text}</p>
        {impact?.justification ? <i>{impact?.justification}</i> : null}
      </>
    );
  };

  return (
    <ListItem style={{ paddingLeft: 2 }}>
      <Popup
        trigger={
          <div>
            <span>
              {icon ? (
                <Icon size="small" icon={icon} />
              ) : impactType ? (
                <Icon size="small" icon={getImpactTypeIcon(impactType)} />
              ) : null}
              {impactName}
              {" - "}
              {impactEffect === "cost"
                ? "Costs "
                : impactEffect === "saving"
                  ? "Saving "
                  : ""}
              {impactEffect ? "up to " : "Up to "}
              {usingValue}
            </span>
          </div>
        }
      >
        {generateImpactText({
          value: value ?? "",
          frequency: "annually",
          ownerImpact: {
            name: impactName,
            type: impactType,
            effect: impactEffect,
          } as OrganisationImpact,
        } as LaneImpactInput)}
      </Popup>
    </ListItem>
  );
};

type ICustomLaneHeader = {
  dragRef: React.RefObject<HTMLDivElement | null>;
  lane: ILaneData;
};

const CustomLaneHeader = ({ dragRef, lane }: ICustomLaneHeader) => {
  const {
    forId,
    canEditProjectBoard,
    selectIdeas,
    selectedIdeas,
    isSelecting,
  } = useContext(ProjectBoardContext);

  const user = useCurrentUser();

  const { id, title, description, cards } = lane;
  const allIdeasSelected =
    cards.length > 0 &&
    cards.every(
      (card) =>
        !!selectedIdeas.find(
          (selectedIdea) => selectedIdea._id === card.idea._id,
        ),
    );

  const onSelect = () => {
    selectIdeas(
      lane.cards.map((card) => card.idea),
      !allIdeasSelected,
    );
  };

  const [editingLaneName, setEditingLaneName] = useState(false);
  const [newLaneName, setNewLaneName] = useState(title ?? "");
  const [savingNewLaneName, setSavingNewLaneName] = useState(false);

  const queryParams = useIdeaQueryParams("board");
  const debouncedImpacts = useThrottle(api.challenges.getIdeaImpacts, 400, [], {
    useLeadingCall: true,
  });
  const ideaImpactsQuery = useQuery(
    initUseQuery(debouncedImpacts, [forId, queryParams], {
      select: (data) => data.laneImpactMeasures?.[lane.id] ?? [],
    }),
  );
  const laneImpactMeasures = ideaImpactsQuery.data ?? [];

  const [editingLaneDescription, setEditingLaneDescription] = useState(false);
  const [newLaneDescription, setNewLaneDescription] = useState(
    description ?? "",
  );
  const [savingNewLaneDescription, setSavingNewLaneDescription] =
    useState(false);

  const { t } = useTranslation();

  const challengeQuery = useQuery(initUseQuery(api.challenges.get, [forId]));
  const challenge = challengeQuery.data;
  const ideaStatuses = challenge?.ownerOrganisation?.ideaStatuses ?? [];
  const laneStatusDef = lane.status
    ? ideaStatuses.find((s) => s._id === lane.status)
    : undefined;

  const updateLane = (
    data: Partial<OpenAPI.Schemas["ProjectLane"]>,
    onComplete = () => {},
    onError = () => {},
  ) => {
    api.boards.updateProjectLane(
      forId,
      id,
      data as ProjectBoardLaneUpdateBody,
      (updatedLane) => {
        if (id === "default") {
          api.setQueryData(api.challenges.get, [forId], (prevChallenge) => ({
            ...(prevChallenge || {}),
            projectBoard: {
              ...(prevChallenge?.projectBoard || {}),
              defaultLane: updatedLane,
            },
          }));
        } else {
          api.invalidateQueries(api.boards.getProjectLanes, [forId]);
        }
        onComplete();
      },
      (err) => {
        toast.error(err.message);
        onError();
      },
    );
  };

  const saveNewLaneName = () => {
    if (newLaneName !== title) {
      setSavingNewLaneName(true);
      updateLane(
        { name: newLaneName },
        () => {
          setEditingLaneName(false);
          setSavingNewLaneName(false);
        },
        () => {
          setEditingLaneName(false);
          setSavingNewLaneName(false);
        },
      );
    } else {
      setEditingLaneName(false);
    }
  };

  const saveNewLaneDescription = () => {
    if (newLaneDescription !== description) {
      setSavingNewLaneDescription(true);
      updateLane(
        { description: newLaneDescription },
        () => {
          setEditingLaneDescription(false);
          setSavingNewLaneDescription(false);
        },
        () => {
          setEditingLaneDescription(false);
          setSavingNewLaneDescription(false);
        },
      );
    } else {
      setEditingLaneDescription(false);
    }
  };

  const reorderLaneIdeas = (sort: string, sortDirection: string) => {
    api.boards.reorderLaneIdeas(
      forId,
      id,
      sort,
      sortDirection,
      () => {
        api.invalidateQueries(api.challenges.getIdeas, [forId]);
      },
      (err) => {
        toast.error(err.message);
      },
    );
  };

  const headerProps = isSelecting
    ? { isSelecting: true, onClick: onSelect }
    : {};

  return (
    <Header {...headerProps}>
      <StyledLaneHeader
        ref={dragRef}
        className={cx(isSelecting && "is-selecting")}
      >
        <HeaderContainer>
          <div style={{ display: "flex", gap: 8, width: "100%" }}>
            {!editingLaneName ? (
              <Label>
                <Icon icon={LightbulbIcon} />
                {cards.length}
              </Label>
            ) : null}

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                width: "100%",
              }}
            >
              {id === "default" && !editingLaneName ? (
                <Popup
                  trigger={
                    <div>
                      <Label
                        className={circleIconLabelClass}
                        color={
                          canEditProjectBoard && title === "Default"
                            ? "yellow"
                            : "grey"
                        }
                        circular
                      >
                        <Icon icon={LockIcon} />
                      </Label>
                    </div>
                  }
                >
                  {
                    <div>
                      This is the default lane, it cannot be deleted or moved to
                      a different position.
                      {canEditProjectBoard && title === "Default" ? (
                        <Label size="small">
                          <Icon icon={InfoIcon} />
                          You can edit the name of this lane
                        </Label>
                      ) : null}
                    </div>
                  }
                </Popup>
              ) : null}

              {editingLaneName ? (
                <Input
                  value={newLaneName}
                  onChange={(e) => setNewLaneName(e.target.value)}
                  autoFocus
                  size="small"
                  fluid
                  style={{ width: "100%", height: 28 }}
                  action={
                    <Button
                      onClick={() => saveNewLaneName()}
                      loading={savingNewLaneName}
                      color="primary"
                    >
                      <Icon icon={CheckIcon} />
                    </Button>
                  }
                />
              ) : (
                <Popup
                  trigger={
                    <StyledTitle
                      className={cx(canEditProjectBoard && "is-editable")}
                      onClick={() =>
                        canEditProjectBoard && setEditingLaneName(true)
                      }
                    >
                      {title}
                    </StyledTitle>
                  }
                >
                  {title}
                </Popup>
              )}
            </div>
          </div>
        </HeaderContainer>

        {canEditProjectBoard ? (
          <DropdownLaneSettings
            lane={lane}
            editLaneName={() => setEditingLaneName(true)}
            editLaneDescription={() => setEditingLaneDescription(true)}
            updateLane={updateLane}
            reorderLaneIdeas={reorderLaneIdeas}
            trigger={
              <Button className={invisibleButtonClass} size="small">
                <Icon icon={EllipsisIcon} />
              </Button>
            }
          />
        ) : null}
      </StyledLaneHeader>

      {laneStatusDef ? (
        <MetaInfo
          text={laneStatusDef.name}
          color={laneStatusDef.color ?? "grey"}
        />
      ) : null}

      {editingLaneDescription ? (
        <div>
          <TextArea
            placeholder="Enter a description for this lane.."
            value={newLaneDescription}
            onChange={(e) => setNewLaneDescription(e.target.value)}
            autoFocus
          />
          <Button
            onClick={() => saveNewLaneDescription()}
            size="small"
            loading={savingNewLaneDescription}
            style={{ marginTop: 5 }}
            fluid
            color="primary"
          >
            <Icon icon={CheckIcon} />
            Save
          </Button>
        </div>
      ) : description ? (
        <LaneDescription>{description}</LaneDescription>
      ) : null}

      {isSelecting && cards?.length ? (
        <div className="selection-box">
          <p>Select all {t("generic.ideas")} in this lane</p>
          <Checkbox isSelected={allIdeasSelected} compact />
        </div>
      ) : null}

      <Accordion
        className={impactAccordionClass}
        styled
        fluid
        style={
          laneImpactMeasures.filter((m) => !!m.laneTotalImpactValue).length
            ? {}
            : { display: "none" }
        }
        panels={[
          {
            key: "total-impact",
            title: "Annual impact",
            content: (...args: unknown[]) => {
              const { active = false } = (args[1] ?? {}) as {
                active?: boolean;
              };
              return (
                <div style={{ paddingRight: 10 }}>
                  <List
                    variant="bulleted"
                    compact
                    style={{
                      display: active ? "block" : "none",
                      marginTop: 0,
                      paddingLeft: 31,
                      paddingRight: 21,
                      maxHeight: 100,
                      overflow: "auto",
                    }}
                  >
                    {laneImpactMeasures
                      .filter((m) => !!m.laneTotalImpactValue)
                      .map((impactMeasure) => {
                        if (impactMeasure.type === "dropdown") {
                          return null;
                        }
                        return (
                          <HeaderImpactMeasure
                            key={impactMeasure._id}
                            icon={getImpactTypeIcon(impactMeasure.type)}
                            value={impactMeasure.laneTotalImpactValue ?? 0}
                            impactType={impactMeasure.type}
                            impactName={impactMeasure.name ?? ""}
                            impactEffect={impactMeasure.effect}
                          />
                        );
                      })}
                  </List>
                </div>
              );
            },
          },
        ]}
      />
    </Header>
  );
};

export default CustomLaneHeader;
